import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./support.html";
import * as kagekiri from 'kagekiri';
import { aiAssetEngagement } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { ElementTitle, ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { getListingPublisher } from '~/modules/helpers/listingHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { selectExtensionListing } from '~/modules/utilities/listing_utils';
import { parseNewLines } from '~/modules/utilities/text';

/** "Support" tab panel: renders provider Details + Additional Help, plus a Contact/Legal card. */
class Support extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.displayTermsAndConditions = false;
  }
  i18nCallback(i18n) {
    this.staticLabels = i18n.providerDetails;
    this.tabLabels = i18n.listingDetails.supportTab;
    this.supportLabels = i18n.support;
    this.modalLabels = i18n.modals.termsAndConditions;
  }
  get provider() {
    return getListingPublisher(this.listing);
  }
  get publisherName() {
    return this.provider?.name ?? '';
  }

  // The listing's own Support Information plugin isn't in ListingResponse's type; cast narrowly to read it.
  get supportInfo() {
    const listingWithSupportInfo = this.listing;
    return listingWithSupportInfo?.['listing/plugins/SupportInformation'];
  }

  // Listing-only: support contact info comes from the listing's Support Information plugin, not the publisher.
  get email() {
    return this.supportInfo?.supportEmailAddress ?? '';
  }
  get mailTo() {
    return this.email ? `mailto:${this.email}` : '';
  }
  get hasEmail() {
    return !!this.mailTo;
  }

  /**
   * The email split so each segment ends at an '@' or '.', letting the
   * template drop a `<wbr>` break opportunity after each boundary. A long
   * address then wraps at the '@' / dot rather than mid-token — the raw
   * string carries no guaranteed break points of its own. A single
   * over-long segment still breaks mid-token as a last resort via the
   * `overflow-wrap` fallback in the CSS.
   */
  get emailSegments() {
    if (!this.email) {
      return [];
    }
    return (this.email.match(/[^@.]*[@.]?/g) ?? []).filter(Boolean).map((text, id) => ({
      id,
      text
    }));
  }
  get phone() {
    return this.supportInfo?.supportPhoneNumber ?? '';
  }
  get hasPhone() {
    return !!this.phone;
  }

  // Listing-only: the partner's knowledge center / support documentation URL, from the
  // listing's Support Information plugin. Gates the "Help" section in the contact card.
  get supportKnowledgeBaseURL() {
    return this.supportInfo?.supportKnowledgeBaseURL ?? '';
  }
  get hasKnowledgeBase() {
    return !!this.supportKnowledgeBaseURL;
  }
  get logo() {
    // @ts-expect-error * publisher/plugins/PublisherLogo is a transformed plugin key, not on BasePublisher
    if (this.provider && this.provider['publisher/plugins/PublisherLogo']) {
      // @ts-expect-error * publisher/plugins/PublisherLogo is a transformed plugin key, not on BasePublisher
      return generateMediaUrl(this.provider['publisher/plugins/PublisherLogo'].Logo);
    }
    return '';
  }
  get hasLogo() {
    return !!this.logo;
  }

  // i18nStore leaves are callable MessageFunctions, so the no-arg default must be invoked, not read.
  get logoAlt() {
    const {
      shared
    } = i18nStore.getState().i18n;
    return this.publisherName ? shared.logoAlt({
      LOGO_NAME: this.publisherName
    }) : shared.deafultLogoAlt();
  }
  get headquarters() {
    return this.provider?.hQLocation ?? '';
  }
  get hasHeadquarters() {
    return !!this.headquarters;
  }
  get employeeCount() {
    // @ts-expect-error * employees is on BasePublisher but not on JapanesePublisherLocalization
    return this.provider?.employees;
  }
  get employees() {
    // Pin to site language, not browser locale: one deployment serves 4 domains, and
    // getUserLanguage can return the dev-only '?' code, which Intl rejects.
    return this.employeeCount != null ? new Intl.NumberFormat(getPreferredLanguage()).format(this.employeeCount) : '';
  }
  get hasEmployees() {
    return this.employeeCount != null && this.employeeCount > 0;
  }
  get description() {
    return parseNewLines(this.provider?.description ?? '');
  }
  get hasDescription() {
    return !!this.description;
  }

  // Free-text "Additional Help" body from the listing's Support Information plugin.
  get supportDescription() {
    return parseNewLines(this.supportInfo?.supportDetails ?? '');
  }
  get hasSupportDescription() {
    return !!this.supportDescription;
  }

  /**
   * True when the provider block above Additional Help renders anything (logo, name, HQ,
   * employees, or description). Gates the divider that sits above the Additional Help
   * heading so, when Additional Help is the only content, that heading is the first thing
   * at the top of the tab rather than trailing a stray hairline.
   */
  get hasProviderDetails() {
    return this.hasLogo || !!this.publisherName || this.hasHeadquarters || this.hasEmployees || this.hasDescription;
  }
  get websiteUrl() {
    return this.provider?.website ?? '';
  }
  get hasWebsite() {
    return !!this.websiteUrl;
  }

  // "{Provider} Website" per Figma; falls back to the plain label when the publisher has no name.
  get websiteLinkLabel() {
    return this.publisherName ? i18nStore.getState().i18n.listingDetails.supportTab.websiteLink({
      PROVIDER_NAME: this.publisherName
    }) : this.staticLabels.website;
  }
  get hasContactInfo() {
    return this.hasPhone || this.hasWebsite || this.hasEmail;
  }

  // Body of the Terms & Conditions modal: the listing's own license agreement, if any.
  get termsAndConditions() {
    return selectExtensionListing(this.listing)?.licenseAgreement ?? '';
  }

  // Gates only the App Terms and Conditions section inside the modal; the Salesforce Terms of
  // Service link above it always applies, so the Terms & Conditions link itself always shows.
  get hasTermsAndConditions() {
    return !!this.termsAndConditions;
  }
  get termsAndConditionsLinkRef() {
    return kagekiri.querySelector('.terms-and-conditions-link');
  }
  openTermsAndConditions(event) {
    event.preventDefault();
    this.displayTermsAndConditions = true;
  }

  /**
   * `onanalytics` enrichers for the Legal Terms & Conditions and Contact Website links
   * (`handleTermsAndConditionsAnalytics` here and `handleWebsiteAnalytics` below).
   *
   * These do NOT dispatch their own event — `wds-link` already fires the `analytics`
   * event (giving it `elementType: 'link'` + `link.callToAction.name`, an allowlisted
   * name in `gtmListingDetailClickType`). Each handler only *adds* the AI-asset
   * `aiAssetAnalytics` payload onto that same bubbling event. Which GA4 event actually
   * lands is then decided by listing type, without any branch here:
   *
   * - **Skill/plugin listings** — `listingDetailV2.handleAnalytics` injects `aiAssetKind`
   *   onto the event, so `GtmSkillEngagementType` / `GtmPluginEngagementType` (which run
   *   before `GtmListingDetailClickType` in the factory) match first and emit
   *   `custEv_skillEngagement` / `custEv_pluginEngagement` — the pre-existing behavior,
   *   preserved unchanged (Cardinal Path's GTM config untouched).
   * - **All other listing types** — the page root never injects `aiAssetKind`, so the
   *   AI-asset handlers skip and the event falls through to `GtmListingDetailClickType`,
   *   which emits `custEv_appEngagement` off the allowlisted link name. This is the
   *   generic fallback that gives apps/consultants coverage they previously lacked.
   *
   * The factory returns the first matching handler, so exactly one GA4 event fires per
   * click; the two paths are mutually exclusive by listing type. The Knowledge Center
   * link intentionally carries no such enricher, so it always takes the
   * `custEv_appEngagement` path.
   */
  handleTermsAndConditionsAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: this.supportLabels.termsAndConditions,
      elementTitle: ElementTitle.SUPPORT_LEGAL,
      elementType: ElementType.LINK_TEXT
    });
  }
  closeTermsAndConditions() {
    this.displayTermsAndConditions = false;
    this.termsAndConditionsLinkRef?.focus();
  }

  // Website counterpart of handleTermsAndConditionsAnalytics above — same enrichment
  // pattern; see its doc comment for how the event self-selects by listing type.
  handleWebsiteAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: this.publisherName ? `${this.publisherName} Website` : 'Website',
      clickUrl: this.websiteUrl,
      elementTitle: ElementTitle.SUPPORT_CONTACT,
      elementType: ElementType.LINK_TEXT
    });
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Support, {
  publicProps: {
    listing: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["displayTermsAndConditions"]
});
const __lwc_component_class_internal = _registerComponent(Support, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;